/**
 * SupportChat Component
 * Customer service chat window matching Attio design
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Button,
  useTheme,
  Paper,
  Slide
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  MoreVertOutlined as MoreVertIcon
} from '@mui/icons-material';

interface SupportChatProps {
  open: boolean;
  onClose: () => void;
}

export const SupportChat: React.FC<SupportChatProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [messages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Welcome! What brings you here today?',
      sender: 'Attio Support',
      time: 'Just now'
    }
  ]);

  const quickActions = [
    'Product question',
    'Setup guidance',
    'Troubleshoot an issue',
    'Talk to Sales'
  ];

  if (!open) return null;

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        pointerEvents: open ? 'auto' : 'none'
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            width: 380,
            height: 600,
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.6)'
              : '0 8px 32px rgba(0, 0, 0, 0.12)'
          }}
        >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mr: -0.5 }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '12px',
                bgcolor: '#3b82f6',
                border: '2px solid white',
                zIndex: 3
              }}
            >
              AI
            </Avatar>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '12px',
                bgcolor: '#10b981',
                border: '2px solid white',
                ml: -1,
                zIndex: 2
              }}
            >
              SH
            </Avatar>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '12px',
                bgcolor: '#f59e0b',
                border: '2px solid white',
                ml: -1,
                zIndex: 1
              }}
            >
              JD
            </Avatar>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '14px' }}>
              Attio
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#10b981'
                }}
              />
              <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                A few hours
              </Typography>
            </Box>
          </Box>

          <IconButton
            size="small"
            sx={{
              borderRadius: '6px',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <MoreVertIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              borderRadius: '6px',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            p: 2.5,
            overflowY: 'auto',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255,255,255,0.01)'
              : 'rgba(0,0,0,0.01)'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '15px',
              mb: 2,
              fontWeight: 500,
              color: 'text.primary'
            }}
          >
            Ask us anything, or share your feedback.
          </Typography>

          {messages.map((message) => (
            <Box key={message.id} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  {message.sender} • {message.time}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.75,
                  borderRadius: '8px',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.04)',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '13px',
                    color: 'text.primary',
                    lineHeight: 1.5
                  }}
                >
                  {message.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Quick Actions */}
        <Box
          sx={{
            p: 2.5,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5,
              mb: 2
            }}
          >
            {quickActions.map((action) => (
              <Button
                key={action}
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 500,
                  py: 1,
                  borderColor: theme.palette.divider,
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                {action}
              </Button>
            ))}
          </Box>

          {/* Privacy Notice */}
          <Box sx={{ position: 'relative' }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '10px',
                color: 'text.secondary',
                lineHeight: 1.4,
                display: 'block'
              }}
            >
              By chatting with us, you agree to our{' '}
              <Typography
                component="span"
                sx={{
                  fontSize: '10px',
                  color: 'primary.main',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                Privacy Policy
              </Typography>
              . Your personal data may be processed per this policy and/or our data processing agreement with the customer entity you are contacting us from.
            </Typography>
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 16,
                height: 16,
                p: 0,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.1)'
                    : 'rgba(0,0,0,0.06)'
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        </Box>
        </Paper>
      </Slide>
    </Box>,
    document.body
  );
};

